import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { EnvelopeSimple } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";

export function JobEmailDialog({ open, onOpenChange, jobId, onSent }) {
  const [to, setTo] = useState("");
  const [presets, setPresets] = useState([]);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open || !jobId) return;
    setLoading(true);
    setSubject("");
    setBody("");
    api
      .get(`/jobs/${jobId}/email/presets`)
      .then(({ data }) => {
        setTo(data.to || "");
        setPresets(data.presets || []);
        if (data.presets?.length) {
          setSubject(data.presets[0].subject || "");
          setBody(data.presets[0].body || "");
        }
      })
      .catch(() => setPresets([]))
      .finally(() => setLoading(false));
  }, [open, jobId]);

  const send = async () => {
    setSending(true);
    try {
      await api.post(`/jobs/${jobId}/email`, { subject: subject.trim(), body: body.trim() });
      toast.success(`Email sent to ${to}`);
      onOpenChange(false);
      onSent?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Could not send email");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="job-email-dialog" className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Email customer</DialogTitle>
        </DialogHeader>
        {to ? (
          <div className="text-xs text-zinc-400" data-testid="job-email-to">
            To: <span className="font-mono-data text-zinc-200">{to}</span>
          </div>
        ) : (
          <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
            This customer has no email on file.
          </div>
        )}
        {presets.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="job-email-presets">
            {presets.map((p, i) => (
              <button
                key={p.key}
                type="button"
                data-testid={`job-email-preset-${i}`}
                onClick={() => {
                  setSubject(p.subject || "");
                  setBody(p.body || "");
                }}
                className="rounded-full border border-violet-500/40 bg-violet-500/10 px-3 py-1.5 text-xs font-semibold text-violet-200 transition-colors duration-150 hover:bg-violet-500/20"
              >
                {p.label}
              </button>
            ))}
          </div>
        )}
        <Input
          data-testid="job-email-subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Subject"
          className="text-sm"
        />
        <Textarea
          data-testid="job-email-body"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={8}
          className="text-sm"
          placeholder="Message to the customer"
        />
        <div className="text-[11px] text-zinc-500">
          {loading ? "Loading templates…" : "Pick a template above or edit before it sends"}
        </div>
        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            data-testid="job-email-cancel"
            onClick={() => onOpenChange(false)}
            disabled={sending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            data-testid="job-email-send"
            onClick={send}
            disabled={sending || loading || !to || !subject.trim() || !body.trim()}
          >
            <EnvelopeSimple size={15} weight="bold" className="mr-1.5" />
            {sending ? "Sending…" : "Send email"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default JobEmailDialog;
